import { useEffect } from 'react';
import {
    FilterCheckboxInForm,
    MainFilterForm,
    QuickFilterCheckboxGroupForm,
    QuickFilterForm,
    QuickFilterFormItem,
    QuickFilterDropdownMultiselectForm,
} from '../ListTableTypes';

export type QuickFilterChangeHandler = (
    value: boolean,
    groupSelector: string,
    key: string,
    immediateFetch?: boolean
) => void;

/** The group's key in the quick-filter state, `selector:comperator` with `=in=` as the default operator. */
export function groupSelectorOf(group: QuickFilterCheckboxGroupForm): string {
    return `${group.selector}${group.comperator !== undefined ? `:${group.comperator}` : ':=in='}`;
}

export function isDropdownMultiselect(filter: QuickFilterFormItem): filter is QuickFilterDropdownMultiselectForm {
    return 'type' in filter && filter.type === 'dropdown-multiselect';
}

export function isGroupDisabled(group: QuickFilterCheckboxGroupForm, mainFilterState: MainFilterForm): boolean {
    return group.getShouldDisable?.({ mainFilters: mainFilterState }) === true;
}

/**
 * Checks or unchecks one option of a checkbox group. Checking an option of a group that is not `multiple`
 * first unchecks the others, without a fetch, so the one fetch below reads the group as a single choice.
 * The group's own `onChange` runs before the fetch, so the main filter it writes into is part of it.
 */
export function toggleQuickFilterOption({
    group,
    option,
    checked,
    handleChange,
    mainFilterState,
    handleMainFilterStateChange,
    immediateFetch = true,
}: {
    group: QuickFilterCheckboxGroupForm;
    option: FilterCheckboxInForm;
    checked: boolean;
    handleChange: QuickFilterChangeHandler;
    mainFilterState: MainFilterForm;
    handleMainFilterStateChange: (state: MainFilterForm) => void;
    immediateFetch?: boolean;
}) {
    const groupSelector = groupSelectorOf(group);
    if (group.multiple !== true && checked) {
        group.filters
            .filter(({ isSelected }) => isSelected)
            .forEach((selected) => handleChange(!selected.value, groupSelector, selected.key, false));
    }
    group.onChange?.({
        isSelected: checked,
        value: option.value,
        groupSelector,
        mainFilterState,
        handleMainFilterStateChange,
    });
    handleChange(checked, groupSelector, option.key, immediateFetch);
}

/**
 * Drops the selection of a group that `getShouldDisable` has disabled: the disabled control alone would
 * leave the query term active with no way left to uncheck it.
 */
export function useClearDisabledQuickFilters(
    quickFilters: QuickFilterForm | undefined,
    mainFilterState: MainFilterForm,
    handleChange: QuickFilterChangeHandler
) {
    useEffect(() => {
        if (!quickFilters) {
            return;
        }
        Object.values(quickFilters).forEach((filter) => {
            if (isDropdownMultiselect(filter) || !isGroupDisabled(filter, mainFilterState)) {
                return;
            }
            (filter.filters ?? [])
                .filter(({ isSelected }) => isSelected)
                .forEach((selected) => handleChange(false, groupSelectorOf(filter), selected.key));
        });
    }, [quickFilters, mainFilterState, handleChange]);
}
